import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useInventoryItem } from '../../hooks/useInventory';
import { inventoryService } from '../../services/inventoryService';
import { useAuth } from '../../context/AuthContext';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import Breadcrumb from '../../components/ui/Breadcrumb';
import { Camera, Package } from 'phosphor-react';

const InventoryForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, businessId } = useAuth();
  const { item, loading: itemLoading, error: itemError } = useInventoryItem(id || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [category, setCategory] = useState<'spare_parts' | 'accessories' | 'devices'>('spare_parts');
  const [description, setDescription] = useState('');
  const [reorderPoint, setReorderPoint] = useState(0);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Load item data for editing
  useEffect(() => {
    if (item) {
      setName(item.name);
      setDescription(item.description || '');
      setCategory(item.category as 'spare_parts' | 'accessories' | 'devices');
      setSku(item.sku || '');
      setReorderPoint(item.reorder_point || 0);
      setCurrentPhotoUrl((item as any).photo_url || '');
    }
  }, [item]);

  // Generate initial SKU for new items
  useEffect(() => {
    if (!id && !sku) {
      const initialSKU = generateSKU(category);
      setSku(initialSKU);
    }
  }, [id, sku, category]);

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image file size must be less than 5MB');
        return;
      }

      setError('');
      setPhotoFile(file);

      // For now, just set a placeholder URL - in a real implementation,
      // you would upload to storage and get the URL back
      const reader = new FileReader();
      reader.onload = (e) => {
        setCurrentPhotoUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateSKU = (selectedCategory: string) => {
    const prefixes: Record<string, string> = {
      spare_parts: 'SP',
      accessories: 'AC',
      devices: 'DV',
    };

    const prefix = prefixes[selectedCategory] || 'IT';
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

    return `${prefix}-${timestamp}${random}`;
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCategory = e.target.value as 'spare_parts' | 'accessories' | 'devices';
    setCategory(newCategory);

    // Auto-generate SKU when category changes (for new items only)
    if (!id) {
      const newSKU = generateSKU(newCategory);
      setSku(newSKU);
    }
  };

  // Show loading while fetching item data or auth/business is loading
  if ((itemLoading && id) || authLoading || (isAuthenticated && !businessId)) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <p className="text-gray-600">
          {(itemLoading && id) ? 'Loading item...' :
           authLoading ? 'Loading authentication...' :
           'Loading business data...'}
        </p>
      </div>
    );
  }

  // Show message if user is authenticated but has no business
  if (isAuthenticated && !businessId) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Business Profile Required</h2>
          <p className="text-gray-600 mb-4">
            You need to create a business profile before you can manage inventory.
          </p>
          <p className="text-sm text-gray-500">
            Please complete your business setup first.
          </p>
        </div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
          <p className="text-gray-600">
            Please log in to access the inventory management system.
          </p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      // Check businessId availability
      if (!businessId) {
        setError('Business information not available. Please try logging out and logging back in.');
        setLoading(false);
        return;
      }

      // Validation
      if (!name.trim()) {
        setError('Item name is required');
        setLoading(false);
        return;
      }
      if (!sku.trim()) {
        setError('SKU or Barcode is required');
        setLoading(false);
        return;
      }

      const formData = {
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        sku: sku.trim(),
        reorder_point: reorderPoint,
      };

      if (id) {
        // Update existing item
        console.log('Updating inventory item:', id, formData);
        const { error: updateError } = await inventoryService.updateInventoryItem(id, formData);
        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }
      } else {
        // Create new item
        console.log('Creating inventory item:', formData);
        const { error: createError } = await inventoryService.createInventoryItem(formData);
        if (createError) {
          console.error('Create error:', createError);
          throw createError;
        }
      }

      navigate('/inventory');
    } catch (err) {
      console.error('Error saving inventory item:', err);
      setError(err instanceof Error ? err.message : 'Failed to save inventory item');
    }

    setLoading(false);
  };

  const breadcrumbItems = id ? [
    { label: 'Inventory', href: '/inventory' },
    { label: item?.name || 'Item', href: `/inventory/${id}` },
    { label: 'Edit Item', current: true }
  ] : [
    { label: 'Inventory', href: '/inventory' },
    { label: 'Add New Item', current: true }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumbs */}
      <Breadcrumb items={breadcrumbItems} />

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {id ? 'Edit Inventory Item' : 'Add New Inventory Item'}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {id ? 'Update item information and details' : 'Create a new inventory item for your business'}
        </p>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <form onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="px-6 py-4 bg-red-50 border-b border-red-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Item Photo Section */}
          <div className="px-6 py-8 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className="w-32 h-32 rounded-full bg-gray-200 border-4 border-white shadow-lg overflow-hidden">
                  {currentPhotoUrl ? (
                    <img
                      src={currentPhotoUrl}
                      alt="Item photo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <Package className="w-12 h-12 text-gray-400" weight="bold" />
                    </div>
                  )}
                  {uploadingImage && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                  disabled={uploadingImage}
                >
                  <Camera className="w-4 h-4" weight="bold" />
                </button>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">Item Photo</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Click the camera icon to upload a photo
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  PNG, JPG, GIF up to 5MB
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>
          </div>

          {/* Form Fields */}
          <div className="px-6 py-8">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              {/* Left Column */}
              <div className="space-y-6">
                <Input
                  label="Item Name *"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Enter item name"
                />

                <Input
                  label="SKU or Barcode *"
                  type="text"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  disabled={!!id} // Disabled when editing
                  required
                  placeholder="Enter SKU or scan barcode"
                />

                <Input
                  label="Reorder Point"
                  type="number"
                  value={reorderPoint}
                  onChange={(e) => setReorderPoint(Number(e.target.value))}
                  min="0"
                  placeholder="Enter reorder point (e.g., 10)"
                />
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <Select
                  label="Category *"
                  value={category}
                  onChange={handleCategoryChange}
                  options={[
                    { value: 'spare_parts', label: 'Spare Parts' },
                    { value: 'accessories', label: 'Accessories' },
                    { value: 'devices', label: 'Device' },
                  ]}
                  disabled={!!id} // Disabled when editing
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    className="input-field resize-none"
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Enter item description (optional)"
                  />
                </div>
              </div>

            </div>
          </div>

          {/* Form Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/inventory')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || uploadingImage}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Saving...
                </div>
              ) : (
                id ? 'Update Item' : 'Create Item'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryForm;